/**
 * Alert Plan-Gate Count Algebra (Story 27.2 — mirrors goalPlanGate.ts / 18.10).
 *
 * PURE LEAF MODULE — no React, no fetch. Both the Alerts page's "Create Alert"
 * button (free scope) and the create dialog's submit guard (per-client scope)
 * share THIS single count function so the two surfaces never disagree, and so
 * the web count mirrors the server's getAlertCount (apps/server/src/services/
 * plan-limits.ts) exactly.
 *
 * 🔴 DUAL SCOPE (matches the server resolver, 27.4):
 *   - free      → TENANT-WIDE total of every non-demo alert (clientId ignored).
 *   - starter   → PER-CLIENT count of non-demo alerts on `clientId`.
 *   - pro       → PER-CLIENT (same as starter).
 *   - agency    → PER-CLIENT (only the 500 safety cap consults it server-side;
 *                 the count here is harmless — agency's alertLimit is null so
 *                 useFeatureGate never blocks below the cap).
 *
 * 🔴 DEMO EXCLUSION: alerts whose client is in demo mode are NEVER counted —
 * server parity with the `c.demo_mode = 0` JOIN. Without it a Free user would
 * see seeded "Demo Client N" alerts eat their 2-alert allowance.
 *
 * 🔴 LOADING: if `alerts` or `clients` is undefined (queries still in flight),
 * the count is `undefined`. The page passes that into useFeatureGate, whose
 * `checked: false` then disables the Create button — creation is never allowed
 * past the gate on an unknown count.
 */

import type { Plan } from "@provenleap/types";

/** Minimal alert shape the gate needs. */
export interface AlertGateInput {
  clientId: string;
}

/** Minimal client shape the gate needs. */
export interface ClientGateInput {
  id: string;
  demoMode: boolean;
}

/**
 * Returns the alert count to compare against the plan's alertLimit, scoped per
 * the plan, with demo-client alerts excluded.
 *
 * @param alerts   - the page's loaded alerts (undefined while loading)
 * @param clients  - the page's loaded clients carrying demoMode (undefined while loading)
 * @param plan     - the tenant's plan (drives the scope)
 * @param clientId - the selected client (required for per-client scope; omitted
 *                   on a paid plan → returns 0 so the page-level button stays
 *                   enabled and the per-client check happens at dialog submit)
 * @returns the scoped, demo-excluded count, or `undefined` while inputs load.
 */
export function countAlertsForGate(
  alerts: readonly AlertGateInput[] | undefined,
  clients: readonly ClientGateInput[] | undefined,
  plan: Plan,
  clientId?: string,
): number | undefined {
  if (alerts === undefined || clients === undefined) return undefined;

  const demoClientIds = new Set(
    clients.filter((c) => c.demoMode).map((c) => c.id),
  );

  const nonDemo = alerts.filter((a) => !demoClientIds.has(a.clientId));

  if (plan === "free") {
    return nonDemo.length;
  }

  if (clientId === undefined) return 0;

  return nonDemo.filter((a) => a.clientId === clientId).length;
}
